import React, { useRef, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import useNotify from '../hooks/useNotify';
import { isEqual } from 'lodash';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { SVG_MODELS } from '../components/svgeditor/SvgLibrary';
import {
    addBulkTicketSeats,
    deleteTicketSeat,
    updateBulkTicketSeats,
} from '@openapi/ticket-seats/ticket-seats';
import mapElementRowToDb from '../pages/seats/helpers/mapElementRowToDb';

const SeatingImportModal = ({ showModal, MODAL_TYPE, showId, elements, onSuccess }) => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);
    const [dryRunResults, setDryRunResults] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const availableModels = Object.keys(SVG_MODELS);

    const closeAction = () => showModal(MODAL_TYPE.NONE);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setFile(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    function calculateStatus(element, type, originalElements) {
        const errors = [];
        const warnings = [];

        if (typeof element.x !== 'number') errors.push(t('seats.validation.xNumber'));
        if (typeof element.y !== 'number') errors.push(t('seats.validation.yNumber'));

        if (typeof element.rotation !== 'number') {
            errors.push(t('seats.validation.rotationNumber'));
        } else if (element.rotation < -360 || element.rotation > 360) {
            errors.push(t('seats.validation.rotationRange'));
        }

        if (element.model && !availableModels.includes(element.model)) {
            errors.push(t('seats.validation.invalidModel', { model: element.model }));
        }

        if (typeof element.bookable !== 'boolean') {
            errors.push(t('seats.validation.bookableBoolean'));
        }

        const existingElement = originalElements.find((el) => Number(el.id) === Number(element.id));

        if (type === 'add' && element.id && !existingElement) {
            warnings.push(t('seats.validation.idNotFound'));
        }

        if (type === 'delete' && !element.id) {
            errors.push(t('seats.validation.missingIdForDelete'));
        }

        if (errors.length > 0) {
            return { level: 'error', message: errors.join(', ') };
        }
        if (warnings.length > 0) {
            return { level: 'warning', message: warnings.join(', ') };
        }
        return { level: 'success' };
    }

    const renderStatusSummary = (rows) => {
        const stats = { success: 0, warning: 0, error: 0 };
        rows.forEach((row) => {
            stats[row.status.level]++;
        });

        return (
            <Box sx={{ display: 'flex', gap: 1.5, ml: 2, alignItems: 'center' }}>
                {stats.success > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: 'success.main',
                        }}
                    >
                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight="bold">
                            {stats.success}
                        </Typography>
                    </Box>
                )}
                {stats.warning > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: 'warning.main',
                        }}
                    >
                        <WarningIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight="bold">
                            {stats.warning}
                        </Typography>
                    </Box>
                )}
                {stats.error > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: 'error.main',
                        }}
                    >
                        <ErrorIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight="bold">
                            {stats.error}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    const parseExcelElement = (excelEl) => {
        const nameI18n = {};
        Object.keys(excelEl).forEach((key) => {
            if (key.startsWith('Name ') && key.length > 5) {
                const lang = key.substring(5).toLowerCase();
                if (excelEl[key] !== undefined && excelEl[key] !== null)
                    nameI18n[lang] = excelEl[key];
            }
        });

        return {
            id: excelEl.Id || excelEl.id,
            bookable:
                excelEl.Bookable === 1 || excelEl.Bookable === true || excelEl.Bookable === 'true',
            name: excelEl.Name,
            nameI18n,
            svg: excelEl.Svg || '',
            x: excelEl.X,
            y: excelEl.Y,
            scale: excelEl.Scale,
            rotation: excelEl.Rotation,
            model: excelEl.Model,
        };
    };

    function calculateChanges(originalElements, newElements) {
        const changes = { toAdd: [], toUpdate: [], toDelete: [] };
        const originalById = new Map(originalElements.map((e) => [Number(e.id), e]));
        const newById = new Map();
        newElements.forEach((e) => {
            if (e.id) newById.set(Number(e.id), e);
        });

        newElements.forEach((e) => {
            if (!e.id || !originalById.has(Number(e.id))) {
                changes.toAdd.push({ ...e, status: calculateStatus(e, 'add', originalElements) });
            }
        });

        originalElements.forEach((e) => {
            if (!newById.has(Number(e.id))) {
                changes.toDelete.push({
                    ...e,
                    status: calculateStatus(e, 'delete', originalElements),
                });
            }
        });

        originalElements.forEach((e) => {
            const newElement = newById.get(Number(e.id));
            if (newElement && !isEqual(e, newElement)) {
                changes.toUpdate.push({
                    old: e,
                    new: newElement,
                    status: calculateStatus(newElement, 'update', originalElements),
                });
            }
        });

        setDryRunResults(changes);
    }

    const renderStatusIcon = (level) => {
        const icons = { error: ErrorIcon, warning: WarningIcon, success: CheckCircleIcon };
        const Icon = icons[level];
        return Icon ? <Icon color={level} sx={{ fontSize: 22 }} /> : null;
    };

    const renderChangesTable = (rows) => (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
                        {t('seats.columns.name')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
                        {t('seats.columns.model')}
                    </TableCell>
                    <TableCell sx={{ width: 80, fontWeight: 'bold', fontSize: '2rem' }}>
                        {t('seats.columns.x')}
                    </TableCell>
                    <TableCell sx={{ width: 80, fontWeight: 'bold', fontSize: '2rem' }}>
                        {t('seats.columns.y')}
                    </TableCell>
                    <TableCell
                        align="right"
                        sx={{ width: 50, fontWeight: 'bold', fontSize: '2rem' }}
                    >
                        {t('seats.import.status')}
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => {
                    const seat = row.new || row;
                    const status = row.status;

                    return (
                        <TableRow key={seat.id || seat.Id}>
                            <TableCell sx={{ fontSize: '1.5rem' }}>
                                {seat.name || seat.Name}
                            </TableCell>
                            <TableCell sx={{ fontSize: '1.5rem' }}>
                                {seat.model || t('seats.import.customModel')}
                            </TableCell>
                            <TableCell sx={{ fontSize: '1.5rem' }}>{seat.x}</TableCell>
                            <TableCell sx={{ fontSize: '1.5rem' }}>{seat.y}</TableCell>
                            <TableCell align="right">
                                <Tooltip title={status.message || ''}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        {renderStatusIcon(status.level)}
                                    </Box>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    const renderUpdateTable = (updates) => {
        const getDiffText = (oldVal, newVal, label) => {
            if (isEqual(oldVal, newVal)) return null;
            return (
                <Typography
                    variant="caption"
                    display="block"
                    key={label}
                    sx={{ fontSize: '1.5rem' }}
                >
                    <strong>{label}:</strong> {String(oldVal)} &rarr; {String(newVal)}
                </Typography>
            );
        };

        return (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
                            {t('seats.columns.name')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
                            {t('seats.import.changes')}
                        </TableCell>
                        <TableCell
                            align="right"
                            sx={{ width: 50, fontWeight: 'bold', fontSize: '2rem' }}
                        >
                            {t('seats.import.status')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {updates.map((update) => {
                        const { old: o, new: n, status } = update;
                        const i18nDiffs = [];
                        const allLangs = new Set([
                            ...Object.keys(o.nameI18n || {}),
                            ...Object.keys(n.nameI18n || {}),
                        ]);
                        allLangs.forEach((lang) => {
                            const oldVal = o.nameI18n?.[lang] || '';
                            const newVal = n.nameI18n?.[lang] || '';
                            const diff = getDiffText(oldVal, newVal, `Name ${lang.toUpperCase()}`);
                            if (diff) i18nDiffs.push(diff);
                        });

                        const diffs = [
                            getDiffText(o.name, n.name, t('seats.columns.name')),
                            getDiffText(o.model, n.model, t('seats.columns.model')),
                            getDiffText(o.bookable, n.bookable, t('seats.columns.bookable')),
                            getDiffText(o.x, n.x, t('seats.columns.x')),
                            getDiffText(o.y, n.y, t('seats.columns.y')),
                            getDiffText(o.scale, n.scale, t('seats.columns.scale')),
                            getDiffText(o.rotation, n.rotation, t('seats.columns.rotation')),
                            ...i18nDiffs,
                        ].filter(Boolean);

                        return (
                            <TableRow key={n.id || n.Id}>
                                <TableCell sx={{ fontSize: '1.5rem' }}>
                                    {n.name || n.Name}
                                </TableCell>
                                <TableCell sx={{ fontSize: '1.5rem' }}>{diffs}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title={status.message || ''}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            {renderStatusIcon(status.level)}
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    };

    const handleContinue = async () => {
        if (!file) return;

        try {
            const XLSX = await import('xlsx');
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });

            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            const rawJsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
            const jsonData = rawJsonData.map(parseExcelElement);

            calculateChanges(elements, jsonData);
        } catch (error) {
            notifyError(error?.message || t('errors.unknownError'));
        }
    };

    const handleConfirm = async () => {
        setIsProcessing(true);
        try {
            const elementsToAdd = dryRunResults.toAdd.filter(
                (seat) => seat.status.level !== 'error',
            );
            const elementsToUpdate = dryRunResults.toUpdate.filter(
                (seat) => seat.status.level !== 'error',
            );
            const elementsToDelete = dryRunResults.toDelete.filter(
                (seat) => seat.status.level !== 'error',
            );

            if (elementsToAdd.length > 0) {
                await addBulkTicketSeats({
                    showId: Number(showId),
                    seats: elementsToAdd.map((element) => {
                        const { id: _id, ...payload } = mapElementRowToDb(element);
                        return payload;
                    }),
                });
            }

            if (elementsToUpdate.length > 0) {
                await updateBulkTicketSeats({
                    showId: Number(showId),
                    seats: elementsToUpdate.map(({ new: element }) => mapElementRowToDb(element)),
                });
            }

            if (elementsToDelete.length > 0) {
                for (const element of elementsToDelete) {
                    await deleteTicketSeat(Number(showId), Number(element.id));
                }
            }

            notifySuccess(t('seats.importSuccess'));
            onSuccess?.();
            closeAction();
        } catch (error) {
            notifyError(error?.message || t('errors.unknownError'));
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Dialog
            open
            onClose={closeAction}
            aria-labelledby="seating-import-modal-title"
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle id="seating-import-modal-title">{t('seats.importTitle')}</DialogTitle>
            <DialogContent>
                {dryRunResults ? (
                    (() => {
                        const hasChanges =
                            dryRunResults.toAdd.length > 0 ||
                            dryRunResults.toUpdate.length > 0 ||
                            dryRunResults.toDelete.length > 0;

                        if (!hasChanges) {
                            return (
                                <Box
                                    sx={{
                                        mt: 2,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        minHeight: '100px',
                                    }}
                                >
                                    <Typography variant="h6" color="text.secondary">
                                        {t('seats.import.noChangesFound')}
                                    </Typography>
                                </Box>
                            );
                        }

                        return (
                            <Box sx={{ mt: 2, maxHeight: '60vh', overflow: 'auto' }}>
                                {dryRunResults.toAdd.length > 0 && (
                                    <Accordion TransitionProps={{ unmountOnExit: true }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography fontWeight="bold" fontSize="2rem">
                                                {t('seats.import.toAdd', {
                                                    count: dryRunResults.toAdd.length,
                                                })}
                                            </Typography>
                                            {renderStatusSummary(dryRunResults.toAdd)}
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            {renderChangesTable(dryRunResults.toAdd)}
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {dryRunResults.toUpdate.length > 0 && (
                                    <Accordion TransitionProps={{ unmountOnExit: true }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography fontWeight="bold" fontSize="2rem">
                                                {t('seats.import.toUpdate', {
                                                    count: dryRunResults.toUpdate.length,
                                                })}
                                            </Typography>
                                            {renderStatusSummary(dryRunResults.toUpdate)}
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            {renderUpdateTable(dryRunResults.toUpdate)}
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {dryRunResults.toDelete.length > 0 && (
                                    <Accordion TransitionProps={{ unmountOnExit: true }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography fontWeight="bold" fontSize="2rem">
                                                {t('seats.import.toDelete', {
                                                    count: dryRunResults.toDelete.length,
                                                })}
                                            </Typography>
                                            {renderStatusSummary(dryRunResults.toDelete)}
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            {renderChangesTable(dryRunResults.toDelete)}
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </Box>
                        );
                    })()
                ) : (
                    <Box
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            border: '2px dashed',
                            borderColor: file ? 'primary.main' : 'grey.400',
                            borderRadius: 2,
                            p: 4,
                            mt: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            bgcolor: 'background.paper',
                            '&:hover': {
                                bgcolor: 'action.hover',
                            },
                            transition: 'background-color 0.2s ease',
                        }}
                    >
                        <input
                            type="file"
                            hidden
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept=".csv,.xlsx,.xls"
                        />
                        <CloudUploadIcon
                            sx={{
                                fontSize: 48,
                                color: file ? 'primary.main' : 'text.secondary',
                                mb: 2,
                            }}
                        />
                        <Typography
                            variant="body1"
                            color="text.primary"
                            align="center"
                            gutterBottom
                        >
                            {file ? file.name : t('seats.upload.uploadTitle')}
                        </Typography>
                        {!file && (
                            <Typography variant="body2" color="text.secondary" align="center">
                                {t('seats.upload.uploadFormats')}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <CommonButton
                    onClick={closeAction}
                    label={t('common.cancel')}
                    buttonType="secondary"
                />
                <CommonButton
                    onClick={dryRunResults ? handleConfirm : handleContinue}
                    disabled={!file || isProcessing}
                    label={dryRunResults ? t('common.save') : t('common.continue')}
                    buttonType="primary"
                />
            </DialogActions>
        </Dialog>
    );
};

export default SeatingImportModal;
